import { useState } from "react";

import { Brain, Search } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "../../components/ui/toaster";
import { ErrorState } from "../../integrations/components/shared/ErrorState";
import { useForgetMemory, useGetUserMemories } from "../hooks";

import type { UserMemory } from "../types";

interface MemoryRowProps {
  id: number;
  body: string;
  isMutating: boolean;
  onForget: (id: number, onError: () => void) => void;
}

const MemoryRow = ({ id, body, isMutating, onForget }: MemoryRowProps) => {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0 border-border/50 bg-destructive/5">
        <span className="text-sm text-muted-foreground line-through">
          {body}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setConfirming(false)}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="h-7 text-xs"
            disabled={isMutating}
            onClick={() => onForget(id, () => setConfirming(false))}
          >
            Forget
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 border-b last:border-b-0 border-border/50 hover:bg-muted/30 transition-colors">
      <span className="text-sm text-foreground">{body}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0"
        disabled={isMutating}
        onClick={() => setConfirming(true)}
      >
        Forget
      </Button>
    </div>
  );
};

interface MemorySectionProps {
  label: string;
  memories: UserMemory[];
  isMutating: boolean;
  onForget: (id: number, onError: () => void) => void;
}

const MemorySection = ({
  label,
  memories,
  isMutating,
  onForget,
}: MemorySectionProps) => {
  if (!memories.length) return null;
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          {label}
        </span>
        <span className="text-[10px] font-semibold bg-muted text-muted-foreground rounded-full px-2 py-0.5">
          {memories.length}
        </span>
      </div>
      <div className="border border-border rounded-lg overflow-hidden">
        {memories.map((m) => (
          <MemoryRow
            key={m.id}
            id={m.id}
            body={m.body}
            isMutating={isMutating}
            onForget={onForget}
          />
        ))}
      </div>
    </div>
  );
};

export const MemoriesSettings = () => {
  const { data: memories = [], isLoading, isError } = useGetUserMemories();
  const forgetMemory = useForgetMemory();
  const [search, setSearch] = useState("");

  const handleForget = (id: number, onError: () => void) => {
    forgetMemory.mutate(id, {
      onError: () => {
        toast({ title: "Failed to forget memory", variant: "destructive" });
        onError();
      },
    });
  };

  const trimmed = search.trim();
  const filtered = trimmed
    ? memories.filter((m) =>
        m.body.toLowerCase().includes(trimmed.toLowerCase()),
      )
    : memories;

  const preferences = filtered.filter((m) => m.kind === "preference");
  const corrections = filtered.filter((m) => m.kind === "correction");
  const hasResults = preferences.length > 0 || corrections.length > 0;

  return (
    <div className="py-8 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Memories</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Things the assistant remembers about your preferences. Add them by
          saying <em>"remember this"</em> or <em>"I prefer…"</em> in chat.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          aria-label="Search memories"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search memories…"
          className="pl-9"
          disabled={isLoading}
        />
      </div>

      <div className="min-h-[200px]">
        {isError ? (
          <ErrorState
            title="Couldn't load memories"
            message="Something went wrong. Please refresh the page and try again."
          />
        ) : isLoading ? (
          <div className="space-y-6 mt-4">
            {[3, 2].map((count, si) => (
              <div key={`skeleton-group-${si}`}>
                <div className="h-3 w-20 bg-muted rounded animate-pulse mb-3" />
                <div className="border border-border rounded-lg overflow-hidden">
                  {Array.from({ length: count }).map((_, i) => (
                    <div
                      key={`skeleton-row-${si}-${i}`}
                      className="flex items-center justify-between px-4 py-3 border-b last:border-b-0 border-border/50"
                    >
                      <div className="h-4 bg-muted rounded animate-pulse w-3/5" />
                      <div className="h-4 bg-muted rounded animate-pulse w-12" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : memories.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <Brain className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium text-muted-foreground">
              No memories yet
            </p>
            <p className="text-xs text-muted-foreground max-w-xs">
              Ask the assistant to remember something during a conversation —
              try <em>"remember I prefer…"</em> or <em>"don't forget…"</em>
            </p>
          </div>
        ) : !hasResults ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">
              No memories match <strong>"{search}"</strong>
            </p>
          </div>
        ) : (
          <>
            <MemorySection
              label="Preferences"
              memories={preferences}
              isMutating={forgetMemory.isPending}
              onForget={handleForget}
            />
            <MemorySection
              label="Corrections"
              memories={corrections}
              isMutating={forgetMemory.isPending}
              onForget={handleForget}
            />
          </>
        )}
      </div>
    </div>
  );
};
